/**
 * Deal Intake — field registry. Single source of truth for what the Review
 * screen renders and how each field is typed/grouped. Mirrors the port spec's
 * 35-write field map (Term_Months is one input → mirrored to Min/Max by the
 * Worker, so it appears here once).
 *
 * `picklistKey` points at the live DealPicklists entry for <select> options
 * (free-text fallback when the live dataType is "text", e.g. Loan_Purpose).
 * `gatedInEnrich` marks Loan_Request_Notes — pulled OUT of the Enrich grid into
 * the previewed opt-in control (never silently merged).
 */
import type { FieldKind } from "../../lib/fieldEquality";

export type FieldGroup =
  | "Borrower contact"
  | "Identification"
  | "Loan"
  | "Property"
  | "Borrower"
  | "Existing debt"
  | "Narrative";

export interface FieldDef {
  key: string; // Zoho API name (form value)
  label: string;
  kind: FieldKind;
  group: FieldGroup;
  /** DealPicklists.fields key for <select> options. */
  picklistKey?: string;
  /** Read-only in the Enrich variant (e.g. Deal_Name — no silent renames). */
  readOnlyInEnrich?: boolean;
  /** Loan_Request_Notes — handled via the opt-in preview, not the field grid. */
  gatedInEnrich?: boolean;
  /** "contact" fields write to a new Contacts record, not the Deal. Default "deal". */
  target?: "deal" | "contact";
}

export const FIELD_SPEC: FieldDef[] = [
  // ── Borrower contact (→ standard Contacts record, linked via Contact_Name) ──
  { key: "First_Name", label: "Borrower first name", kind: "text", group: "Borrower contact", target: "contact" },
  { key: "Last_Name", label: "Borrower last name", kind: "text", group: "Borrower contact", target: "contact" },
  { key: "Phone", label: "Phone", kind: "text", group: "Borrower contact", target: "contact" },
  { key: "Email", label: "Email", kind: "text", group: "Borrower contact", target: "contact" },

  // ── Identification ──
  { key: "Deal_Name", label: "Deal name", kind: "text", group: "Identification", readOnlyInEnrich: true },
  { key: "Stage", label: "Stage", kind: "picklist", group: "Identification", picklistKey: "Stage" },
  { key: "Closing_Date", label: "Closing date", kind: "date", group: "Identification" },

  // ── Loan ──
  { key: "Loan_Amount_Requested", label: "Loan amount", kind: "currency", group: "Loan" },
  { key: "Loan_Product", label: "Loan product", kind: "picklist", group: "Loan", picklistKey: "Loan_Product" },
  { key: "Lien_Position_Requested", label: "Lien position", kind: "picklist", group: "Loan", picklistKey: "Lien_Position_Requested" },
  { key: "LTV", label: "LTV (%)", kind: "number", group: "Loan" },
  { key: "Loan_Purpose", label: "Loan purpose", kind: "picklist", group: "Loan", picklistKey: "Loan_Purpose" },
  { key: "DSCR_Requested", label: "DSCR", kind: "number", group: "Loan" },
  { key: "Term_Months", label: "Term (months)", kind: "number", group: "Loan" },
  { key: "Speed_To_Close_Days", label: "Speed to close (days)", kind: "number", group: "Loan" },

  // ── Property ──
  { key: "Property_Location", label: "Property address", kind: "text", group: "Property" },
  { key: "Property_Type_Scenario", label: "Property type", kind: "picklist", group: "Property", picklistKey: "Property_Type_Scenario" },
  { key: "Property_State_Scenario", label: "State", kind: "picklist", group: "Property", picklistKey: "Property_State_Scenario" },
  { key: "Occupancy", label: "Occupancy", kind: "picklist", group: "Property", picklistKey: "Occupancy" },
  { key: "Projected_Value", label: "Projected value", kind: "currency", group: "Property" },
  { key: "ARV", label: "ARV", kind: "currency", group: "Property" },
  { key: "Rural_Property", label: "Rural property", kind: "boolean", group: "Property" },

  // ── Borrower ──
  { key: "FICO_Score", label: "FICO score", kind: "number", group: "Borrower" },
  { key: "Foreign_National", label: "Foreign national", kind: "boolean", group: "Borrower" },
  { key: "Net_To_Borrower", label: "Net to borrower", kind: "currency", group: "Borrower" },

  // ── Existing debt ──
  { key: "st_Mortgage_Amount", label: "1st TD balance", kind: "currency", group: "Existing debt" },
  { key: "Rate", label: "1st TD rate (%)", kind: "number", group: "Existing debt" },
  { key: "st_Mortgage_Pay_Off", label: "Pay off 1st TD", kind: "boolean", group: "Existing debt" },
  { key: "nd_Mortgage_Amount", label: "2nd TD balance", kind: "currency", group: "Existing debt" },
  { key: "Rate_2nd", label: "2nd TD rate (%)", kind: "number", group: "Existing debt" },
  { key: "nd_Mortgage_Pay_Off", label: "Pay off 2nd TD", kind: "boolean", group: "Existing debt" },
  { key: "rd_Mortgage_Amount", label: "3rd TD balance", kind: "currency", group: "Existing debt" },
  { key: "Rate_3rd", label: "3rd TD rate (%)", kind: "number", group: "Existing debt" },
  { key: "rd_Mortgage_Pay_Off", label: "Pay off 3rd TD", kind: "boolean", group: "Existing debt" },

  // ── Narrative ──
  { key: "Use_of_Funds", label: "Use of funds", kind: "multiline", group: "Narrative" },
  { key: "Exit_Strategy", label: "Exit strategy", kind: "multiline", group: "Narrative" },
  { key: "Rental_Income_Notes", label: "Rental income notes", kind: "multiline", group: "Narrative" },
  { key: "Loan_Request_Notes", label: "Lender paragraph (Loan_Request_Notes)", kind: "multiline", group: "Narrative", gatedInEnrich: true },
];

export const FIELD_GROUPS: FieldGroup[] = [
  "Borrower contact", "Identification", "Loan", "Property", "Borrower", "Existing debt", "Narrative",
];

export const BOOLEAN_KEYS = new Set(FIELD_SPEC.filter((f) => f.kind === "boolean").map((f) => f.key));
